import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import MobileFilterPanel from "../components/MobileFilterPanel";
import { toast } from "sonner";
import { useApplication } from "../context/ApplicationContext";

const PERIOD_LABELS = {
  day: "Today",
  week: "Last 7 days",
  month: "Last 30 days",
  academic: "This academic year",
};

function totalStageClosures(period) {
  return Object.values(period?.stage_closures || {}).reduce((sum, v) => sum + (v || 0), 0);
}

function totalRepairActions(period) {
  return Object.values(period?.repair_actions || {}).reduce((sum, v) => sum + (v || 0), 0);
}

function userPeriod(user, period) {
  return user.periods?.[period] || { stage_closures: {}, inspection_failures: {}, repair_actions: {} };
}

export default function Statistics() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("all");
  const [period, setPeriod] = useState("day");
  const [range, setRange] = useState({from:"", to:""});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { effectiveWorkflow } = useApplication();

  useEffect(() => {
    api.get("/sites")
      .then(({ data }) => setSites(data))
      .catch(() => setSites([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    if (period === "custom" && (!range.from || !range.to)) { setLoading(false); return; }
    api.get("/stats/user-activity", { params: { site_id: siteId, from_date: period === "custom" ? range.from : undefined, to_date: period === "custom" ? range.to : undefined } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Statistics failed to load"))
      .finally(() => setLoading(false));
  }, [siteId, period, range.from, range.to]);

  const stages = useMemo(() => (effectiveWorkflow?.stages || []).filter(stage => stage.enabled).map(stage => stage.key), [effectiveWorkflow]);
  const stageLabels = useMemo(() => Object.fromEntries((effectiveWorkflow?.stages || []).map(stage => [stage.key, stage.display_name])), [effectiveWorkflow]);
  const users = useMemo(() => data?.users || [], [data?.users]);
  const totals = useMemo(() => {
    const out = { stage: 0, repair: 0, failures: 0, activeUsers: 0 };
    users.forEach((u) => {
      const p = userPeriod(u, period);
      const stageTotal = totalStageClosures(p);
      const repairTotal = totalRepairActions(p);
      out.stage += stageTotal;
      out.repair += repairTotal;
      out.failures += Object.values(p.inspection_failures || {}).reduce((sum, v) => sum + (v || 0), 0);
      if (stageTotal || repairTotal) out.activeUsers += 1;
    });
    return out;
  }, [users, period]);
  const rankedUsers = useMemo(() => users
    .map((u) => {
      const p = userPeriod(u, period);
      return { ...u, stageTotal: totalStageClosures(p), repairTotal: totalRepairActions(p) };
    })
    .map((u) => ({ ...u, combinedTotal: u.stageTotal + u.repairTotal }))
    .sort((a, b) => b.combinedTotal - a.combinedTotal || String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""))), [users, period]);
  const topUser = rankedUsers.find((u) => u.combinedTotal > 0);
  const maxCombinedTotal = rankedUsers[0]?.combinedTotal || 0;
  const averageActions = totals.activeUsers ? Math.round((totals.stage + totals.repair) / totals.activeUsers) : 0;
  const stageBreakdown = stages.map(stage => [stage, users.reduce((sum,user)=>sum+(userPeriod(user,period).stage_closures?.[stage]||0),0)]).filter(([,count])=>count>0);
  const repairBreakdown = ["raised","completed","escalated","duplicate"].map(key=>[key,users.reduce((sum,user)=>sum+(userPeriod(user,period).repair_actions?.[key]||0),0)]);
  const maxStage = Math.max(1,...stageBreakdown.map(([,count])=>count));

  return (
    <div data-testid="statistics-page" className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-chivo font-black text-4xl tracking-tight">Statistics</h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Admin/staff activity totals, top contributors and inspection rework signals by user.
          </p>
        </div>
        <MobileFilterPanel className="bg-white border border-gray-200 p-3 flex-wrap gap-3 items-end" testId="statistics-filters">
          <label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
            Site
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              data-testid="stats-site-filter"
              className="mt-1 block border border-gray-300 bg-white px-3 py-2 text-xs min-w-[140px]"
            >
              <option value="all">All sites</option>
              {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
          </label>
          <label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
            Period
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="stats-period-filter"
              className="mt-1 block border border-gray-300 bg-white px-3 py-2 text-xs min-w-[140px]"
            >
              {[...Object.entries(PERIOD_LABELS).map(([key, label]) => ({ key, label })), {key:"custom",label:"Custom range"}].map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </label>
          {period === "custom" && <><label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">From<input aria-label="From" type="date" value={range.from} onChange={e=>setRange({...range,from:e.target.value})} className="mt-1 block min-h-11 rounded-lg border px-3"/></label><label className="text-[10px] font-mono uppercase tracking-wider text-gray-500">To<input aria-label="To" type="date" min={range.from} value={range.to} onChange={e=>setRange({...range,to:e.target.value})} className="mt-1 block min-h-11 rounded-lg border px-3"/></label></>}
        </MobileFilterPanel>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-xl border bg-white p-5" data-testid="stats-workflow"><h2 className="font-chivo text-xl font-black">Workflow</h2><p className="mb-4 text-xs text-slate-500">Recorded stage completions in the selected period.</p>{stageBreakdown.length?stageBreakdown.map(([stage,count])=><div key={stage} className="mb-3"><div className="flex justify-between text-xs font-bold"><span>{stageLabels[stage]||data?.stage_labels?.[stage]||"Workflow stage"}</span><span>{count}</span></div><div className="mt-1 h-2 bg-slate-100"><div className="h-full bg-slate-900" style={{width:`${Math.round(count/maxStage*100)}%`}}/></div></div>):<p className="text-sm text-slate-500">No workflow completions recorded.</p>}</section>
        <section className="rounded-xl border bg-white p-5" data-testid="stats-repairs"><h2 className="font-chivo text-xl font-black">Repairs</h2><p className="mb-4 text-xs text-slate-500">Supported repair actions in the selected period.</p><div className="grid grid-cols-2 gap-3">{repairBreakdown.map(([key,count])=><div key={key} className="rounded-lg border p-3"><span className="block text-[10px] font-bold uppercase text-slate-500">{key}</span><strong className="text-2xl">{count}</strong></div>)}</div></section>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border border-gray-200 bg-white">
        <Stat label="Stage closures" value={totals.stage} />
        <Stat label="Repair actions" value={totals.repair} />
        <Stat label="Active users" value={totals.activeUsers} />
        <Stat label="Avg actions / active user" value={averageActions} />
        <Stat label="Inspection fails" value={totals.failures} tone={totals.failures ? "red" : undefined} />
      </div>

      {topUser && (
        <div className="bg-white border border-gray-200 p-4" data-testid="stats-leader-summary">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Most active in selected period</div>
          <div className="mt-1 font-chivo font-black text-xl">{topUser.name || topUser.email}</div>
          <div className="text-xs font-mono text-gray-600 mt-1">
            {topUser.combinedTotal} total action{topUser.combinedTotal === 1 ? "" : "s"} · {topUser.stageTotal} stage · {topUser.repairTotal} repair
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center text-sm text-gray-500">
          No activity found for this site and period.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="stats-table">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">Stage closures</th>
                <th className="px-3 py-2">Repair actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rankedUsers.map((u) => {
                const p = userPeriod(u, period);
                const barWidth = maxCombinedTotal && u.combinedTotal ? `${Math.max(6, Math.round((u.combinedTotal / maxCombinedTotal) * 100))}%` : "0%";
                const stageEntries = stages
                  .map((stage) => [stage, p.stage_closures?.[stage] || 0])
                  .filter(([, count]) => count > 0);
                const repairEntries = Object.entries(p.repair_actions || {}).filter(([, count]) => count > 0);
                return (
                  <tr key={u.user_id}>
                    <td className="px-3 py-3 align-top min-w-[180px]">
                      <div className="font-chivo font-bold">{u.name || u.email}</div>
                      <div className="text-[10px] font-mono text-gray-500 uppercase">{u.email || "—"} · {u.role || "—"}</div>
                    </td>
                    <td className="px-3 py-3 align-top min-w-[160px]">
                      <div className="font-mono text-xs font-bold">{u.combinedTotal}</div>
                      <div className="mt-2 h-2 bg-gray-100 border border-gray-200">
                        <div className="h-full bg-black" style={{ width: barWidth }} />
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top min-w-[320px]">
                      <div className="font-mono text-xs font-bold mb-1">Total: {totalStageClosures(p)}</div>
                      {stageEntries.length === 0 ? (
                        <span className="text-xs text-gray-500">No stage closures</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {stageEntries.map(([stage, count]) => (
                            <span key={stage} className="border border-gray-300 bg-gray-50 px-2 py-1 text-[10px] font-mono uppercase">
                              {stageLabels[stage] || data?.stage_labels?.[stage] || "Workflow stage"}: {count}
                            </span>
                          ))}
                        </div>
                      )}
                      {(p.inspection_failures?.cleaning_inspection || p.inspection_failures?.refurbishment_inspection) ? (
                        <div className="mt-2 text-[10px] font-mono text-red-700">
                          Failed inspections counted: Cleaning {p.inspection_failures?.cleaning_inspection || 0}, Refurb {p.inspection_failures?.refurbishment_inspection || 0}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 align-top min-w-[260px]">
                      <div className="font-mono text-xs font-bold mb-1">Total: {totalRepairActions(p)}</div>
                      {repairEntries.length === 0 ? (
                        <span className="text-xs text-gray-500">No repair actions</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {repairEntries.map(([key, count]) => (
                            <span key={key} className="border border-gray-300 bg-gray-50 px-2 py-1 text-[10px] font-mono uppercase">
                              {key}: {count}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const valueClass = tone === "red" ? "text-red-700" : "text-black";
  return (
    <div className="p-4 border-r border-gray-200 last:border-r-0">
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-chivo font-black text-3xl mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}
